import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import type { HeatmapNode } from "../../features/kquant/types";
import { MarketHeatmapCard } from "../../features/kquant/components/MarketHeatmapCard";
import { streamNewsAnalysis, type SectorImpact } from "../../features/kquant/api/financeChat";
import type { SectorNewsItem, SectorPanelData } from "./HotTopicsSection.types";
import { MessageContent } from "./MessageContent";

function sectorsToHeatmapNode(sectors: SectorImpact[]): HeatmapNode {
  return {
    name: "Market Impact",
    value: sectors.length,
    children: sectors.map((s) => ({
      name: s.sector,
      value: Math.max(1, Math.abs(s.change_percent) * 10 + 1),
      changePct: s.change_percent,
    })),
  };
}

export function NewsAnalysisOverlay({
  news,
  panel,
  onClose,
}: {
  news: SectorNewsItem;
  panel: SectorPanelData;
  onClose: () => void;
}) {
  // Article state
  const [articleText, setArticleText] = useState<string | null>(null);
  const [articleLoading, setArticleLoading] = useState(true);
  const [articleError, setArticleError] = useState<string | null>(null);

  // AI analysis state
  const [thinkingContent, setThinkingContent] = useState("");
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [heatmapNode, setHeatmapNode] = useState<HeatmapNode | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>("fetching_article");

  const abortRef = useRef<AbortController | null>(null);

  // Fetch article text
  useEffect(() => {
    if (!news.url) {
      setArticleLoading(false);
      setArticleText(news.summary ?? null);
      return;
    }
    setArticleLoading(true);
    setArticleError(null);
    const ac = new AbortController();

    fetch(`/api/v1/finance/news/content?url=${encodeURIComponent(news.url)}`, {
      signal: ac.signal,
      headers: { Accept: "application/json" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json() as Promise<{ text: string; title: string; word_count: number }>;
      })
      .then((data) => {
        setArticleText(data.text || news.summary || null);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setArticleText(news.summary ?? null);
        setArticleError("Could not load full article. Showing summary.");
      })
      .finally(() => {
        setArticleLoading(false);
      });

    return () => ac.abort();
  }, [news.url, news.summary]);

  // Stream AI analysis
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    setAnalysisLoading(true);
    setAnalysisError(null);

    streamNewsAnalysis(
      {
        news_url: news.url,
        news_title: news.title,
        news_summary: news.summary ?? "",
        sector_id: panel.sectorId,
        sector_label: panel.label,
      },
      {
        signal: ac.signal,
        onEvent: (evt) => {
          if ("error" in evt) {
            setAnalysisError(String((evt as { error: string }).error));
            setAnalysisLoading(false);
            return;
          }
          if (evt.type === "stage") setStage(evt.stage);
          if (evt.type === "thinking") setThinkingContent((prev) => prev + evt.content);
          if (evt.type === "message") setMessageContent((prev) => prev + evt.content);
          if (evt.type === "heatmap_data") {
            setHeatmapNode(sectorsToHeatmapNode(evt.sectors));
          }
          if (evt.type === "done") setAnalysisLoading(false);
        },
      }
    ).catch((err: unknown) => {
      if (err instanceof Error && err.name === "AbortError") return;
      setAnalysisError(err instanceof Error ? err.message : "Analysis failed");
      setAnalysisLoading(false);
    });

    return () => ac.abort();
  }, [news.url, news.title, news.summary, panel.sectorId, panel.label]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    onClose();
  }, [onClose]);

  const stageLabel: Record<string, string> = {
    fetching_article: "Fetching article...",
    analyzing: "Analyzing news...",
    reasoning: "Reasoning...",
    message: "Writing analysis...",
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight { from { opacity: 0; transform: translateX(15px); } to { opacity: 1; transform: scale(1) translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .news-anim-slide { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .news-anim-fade { animation: fadeIn 0.3s ease-out forwards; }
        .news-scrollbar-hide::-webkit-scrollbar { display: none; }
        .news-scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="fixed inset-0 z-50 flex news-anim-slide bg-white">
        {/* Left panel: article content */}
        <div className="flex flex-col w-[58%] h-full border-r border-slate-200 bg-white">
          {/* Header */}
          <div className="h-14 shrink-0 border-b border-slate-200 flex items-center px-4 gap-3 bg-white/95 backdrop-blur-sm">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors rounded-lg px-2 py-1.5 hover:bg-slate-100"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
            <div className="h-4 w-px bg-slate-200" />
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
              {panel.label}
            </span>
          </div>

          {/* Article body */}
          <div className="flex-1 overflow-y-auto news-scrollbar-hide px-8 py-6">
            <h1 className="text-xl font-bold text-slate-900 leading-snug mb-4">
              {news.title}
            </h1>

            {articleLoading ? (
              <div className="space-y-3 mt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 rounded bg-slate-100 animate-pulse"
                    style={{ width: `${75 + (i % 3) * 10}%` }}
                  />
                ))}
              </div>
            ) : (
              <>
                {articleError && (
                  <div className="mb-3 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {articleError}
                  </div>
                )}
                {articleText ? (
                  <div className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap news-anim-fade">
                    {articleText}
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 italic">No article content available.</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right panel: AI analysis */}
        <div className="flex flex-col w-[42%] h-full bg-slate-50">
          {/* Header */}
          <div className="h-14 shrink-0 border-b border-slate-200 flex items-center px-5 gap-2 bg-white">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-sm font-semibold text-slate-800">AI Analysis</span>
            {analysisLoading && (
              <div className="flex items-center gap-1.5 ml-auto text-xs text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {stageLabel[stage] ?? "Analyzing..."}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto news-scrollbar-hide px-5 py-5 space-y-4">
            {/* Thinking block */}
            {thinkingContent && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden news-anim-fade">
                <button
                  type="button"
                  onClick={() => setThinkingExpanded((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <span>Reasoning trace</span>
                  <span className="text-slate-400">{thinkingExpanded ? "▲" : "▼"}</span>
                </button>
                {thinkingExpanded && (
                  <div className="px-4 pb-3 text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3 max-h-48 overflow-y-auto news-scrollbar-hide whitespace-pre-wrap">
                    {thinkingContent}
                  </div>
                )}
              </div>
            )}

            {/* Message content */}
            {messageContent && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-800 leading-relaxed news-anim-fade">
                <MessageContent content={messageContent} theme="light" />
              </div>
            )}

            {/* Loading skeleton for message */}
            {analysisLoading && !messageContent && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-3.5 rounded bg-slate-100 animate-pulse"
                    style={{ width: `${60 + (i % 4) * 10}%` }}
                  />
                ))}
              </div>
            )}

            {/* Heatmap */}
            {heatmapNode && (
              <div className="news-anim-fade">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  Predicted Sector Impact
                </div>
                <MarketHeatmapCard
                  autoFetch={false}
                  heatmap={heatmapNode}
                  theme="light"
                  className="!shadow-none"
                />
              </div>
            )}

            {/* Error state */}
            {analysisError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {analysisError}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}